import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { Brand } from '../types'
import { useAuth } from './AuthContext'
import { loadBrands } from '../services/brandStore'
import { brandOf } from '../utils/brand'

/**
 * Stellt die für die Session sichtbaren Brands bereit (einmal geladen) und löst
 * `brandKey → Brand` für Anzeige-Chips/Vorschauen auf. Ersetzt die frühere
 * Mock-Auflösung (`resolveBrand`).
 *
 * Hinweis: Die Rolle `user` darf die Brand-Endpunkte nicht abrufen (admin-only)
 * → `brands` bleibt leer, `resolveBrand` liefert dann einen neutralen Platzhalter
 * (Name = brandKey). Die eigene vCard-Vorschau des Users zeigt daher den brandKey
 * ohne Markenfarbe/-name (Backend liefert diese im Editor-Endpunkt nicht mit).
 */
interface BrandContextValue {
  brands: Brand[]
  loading: boolean
  resolveBrand: (brandKey?: string | null) => Brand
  refresh: () => Promise<void>
}

const BrandContext = createContext<BrandContextValue | null>(null)

export function BrandProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth()
  const [brands, setBrands] = useState<Brand[]>([])
  const [loading, setLoading] = useState(false)

  const load = useCallback(async () => {
    if (!session || session.role === 'user') {
      setBrands([])
      return
    }
    setLoading(true)
    try {
      setBrands(await loadBrands(session))
    } catch {
      setBrands([])
    } finally {
      setLoading(false)
    }
  }, [session])

  useEffect(() => {
    void load()
  }, [load])

  const value = useMemo<BrandContextValue>(
    () => ({
      brands,
      loading,
      resolveBrand: (brandKey) => brandOf(brands, brandKey),
      refresh: load,
    }),
    [brands, loading, load],
  )

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>
}

export function useBrands(): BrandContextValue {
  const ctx = useContext(BrandContext)
  if (!ctx) throw new Error('useBrands must be used within a BrandProvider')
  return ctx
}
